import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import type {
  IMenuCarmiData,
  IMenuCssVars,
  IMenuDefinition,
  IMenuMapperProps,
  IMenuStateRefs,
  ItemTextAlignmentType,
  MenuAnimationNameType,
  MenuDisplayModeType,
  MenuJustificationType,
  MenuOrientationType,
  MenuOverflowType,
  MenuVerticalDropdownDisplayType,
} from '../Menu.types';
import {
  displayModeToCssVarsMap,
  orientationToCssVarsMap,
  dropdownDisplayTypeToCssVarMap,
  verticalOverflowCssVars,
  horizontalOverflowToCssVarsMap,
  justificationToCssVarsMap,
  itemTextAlignmentToCssVarsMap,
  animationNameToCssVarsMap,
  getDirectionCssVars,
  hamburgerMenuAnimationNameToCssVarsMap,
} from './utils/MapperUtils';
import type { IMenuSDKData } from '@wix/editor-elements-corvid-utils/src/props-factories/menuUtils';
import {
  MenuOverflow,
  MenuOrientation,
  VerticalDropdownDisplay,
  HamburgerMenuAnimationName,
} from '../constants';
import { ANIMATION_PACKAGE_CONFIG, defaultTranslations } from './constants';

export const props = withCompInfo<
  IMenuMapperProps,
  IMenuDefinition,
  IMenuCarmiData
>()(['compData', 'translate'], ({ compData, translate }, carmiData) => {
  return {
    ...carmiData,
    ...compData,
    translations: {
      menuNavAriaLabel:
        translate(
          'ariaLabels',
          'dropDownMenu_AriaLabel_TopLevel_SiteNavigation',
        ) || defaultTranslations.menuNavAriaLabel,
      dropdownButtonAriaLabel:
        translate('ariaLabels', 'menu_dropdown_chevron_button_aria_label') ||
        defaultTranslations.dropdownButtonAriaLabel,
    },
  };
});

export const css = withCompInfo<IMenuCssVars, IMenuDefinition>()(
  ['compData', 'styleProperties'],
  ({ compData, styleProperties }) => {
    const {
      direction,
      submenuDirection = 'inherit',
      itemDirection = 'inherit',
      submenuItemDirection = 'inherit',
      submenuSubItemDirection = 'inherit',
    } = compData;
    const {
      orientation = MenuOrientation.Horizontal,
      'vertical-dropdown-display':
        verticalDropdownDisplay = VerticalDropdownDisplay.AlwaysOpen,
      overflow = MenuOverflow.Scroll,
      'display-mode': displayMode,
      'menu-items-justification': justification = 'none',
      'item-text-align': itemTextAlign = 'center',
      'animation-name': animationName,
    } = styleProperties;

    const menuAnimatioPackage = animationName as MenuAnimationNameType;

    const hamburgerMenuAnimationName = menuAnimatioPackage
      ? ANIMATION_PACKAGE_CONFIG[menuAnimatioPackage].hamburgerMenuAnimationName
      : HamburgerMenuAnimationName.None;

    const isVerticalOrientation = orientation === MenuOrientation.Vertical;
    return {
      '--direction': direction,
      '--item-direction': itemDirection,
      '--dropdown-menu-direction': submenuDirection,
      '--dropdown-menu-item-direction': submenuItemDirection,
      '--dropdown-menu-sub-item-direction': submenuSubItemDirection,
      ...getDirectionCssVars(direction),
      ...displayModeToCssVarsMap[displayMode as MenuDisplayModeType],
      ...orientationToCssVarsMap[orientation as MenuOrientationType],
      ...justificationToCssVarsMap[justification as MenuJustificationType],
      ...itemTextAlignmentToCssVarsMap[itemTextAlign as ItemTextAlignmentType],
      ...animationNameToCssVarsMap[menuAnimatioPackage],
      ...hamburgerMenuAnimationNameToCssVarsMap[hamburgerMenuAnimationName],
      ...(isVerticalOrientation
        ? {
            ...verticalOverflowCssVars,
            ...dropdownDisplayTypeToCssVarMap[
              verticalDropdownDisplay as MenuVerticalDropdownDisplayType
            ],
          }
        : horizontalOverflowToCssVarsMap[overflow as MenuOverflowType]),
    };
  },
);

export const sdkData = withCompInfo<IMenuSDKData, IMenuDefinition>()(
  ['pagesMap'],
  ({ pagesMap }) => {
    return {
      pageList: pagesMap,
    };
  },
);

export const stateRefs = withStateRefsValues<keyof IMenuStateRefs>([
  'currentUrl',
]);

export default createComponentMapperModel({
  css,
  props,
  stateRefs,
  sdkData,
});
